// F05 — Levende kart med spor (MapLibre GL)
// Editorial papir-stil: bilrute solid maple, cruise stiplet navy,
// dager som nummererte pinner. Dagens stopp pulserer i maple.

import { DAYS, PLACE_IMG } from '../data.js';
import { esc, safeUrl } from '../utils.js';

// Lokalt utsiktsbilde for et POI-navn (samme oppslag som dagsplanen).
function poiLocalImg(name){
  if(!name) return '';
  for(const [re,src] of PLACE_IMG){ if(re.test(name)) return src; }
  return '';
}

const COLORS = {
  paper:  '#F2EBDB',
  navy:   '#0E2238',
  aurora: '#2E6B5C',
  maple:  '#B84A2E',
  gold:   '#C99A3B',
  ink:    'rgba(14,34,56,.62)',
};

// Tre raster-kilder kombineres for editorial papir-stil med terrain:
// 1) cartocdn voyager (med labels) — base m/ stedsnavn
// 2) OpenTopoMap — terrain m/ hillshading og høydekoter
// 3) cartocdn voyager_only_labels — labels på toppen så de ikke drukner
const VOYAGER         = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png';
const VOYAGER_LABELS  = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png';
const OPENTOPO        = 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png';

let mapInstance = null;
let markerEls = [];

function buildStyle(){
  return {
    version: 8,
    sources: {
      'paper-tiles': {
        type: 'raster',
        tiles: ['a','b','c','d'].map(s => VOYAGER.replace('{s}', s)),
        tileSize: 256,
        attribution: '© OpenStreetMap · Carto',
      },
      'topo': {
        type: 'raster',
        tiles: ['a','b','c'].map(s => OPENTOPO.replace('{s}', s)),
        tileSize: 256,
        maxzoom: 14,
        attribution: '© OpenTopoMap (CC-BY-SA)',
      },
      'labels': {
        type: 'raster',
        tiles: ['a','b','c','d'].map(s => VOYAGER_LABELS.replace('{s}', s)),
        tileSize: 256,
      },
    },
    layers: [
      { id: 'paper-bg', type: 'background', paint: { 'background-color': COLORS.paper } },
      // Base — desaturert papir-tone
      { id: 'paper-tiles', type: 'raster', source: 'paper-tiles',
        paint: { 'raster-opacity': 0.55, 'raster-saturation': -0.5, 'raster-contrast': 0.05 } },
      // Terrain overlay — hillshading og høydekoter på toppen, lav opasitet
      { id: 'topo', type: 'raster', source: 'topo',
        paint: { 'raster-opacity': 0.32, 'raster-saturation': -0.6, 'raster-brightness-min': 0.15 } },
      // Stedsnavn øverst så de ikke drukner i terrain
      { id: 'labels', type: 'raster', source: 'labels',
        paint: { 'raster-opacity': 0.85, 'raster-contrast': 0.10 } },
    ],
  };
}

// Inside Passage cruise-rute — Celebrity Solstice følger Strait of
// Georgia → Johnstone Strait → Queen Charlotte Sound → Hecate Strait
// → Dixon Entrance → Chatham Strait → Icy Strait. Tett waypoints så
// polylinjen hugger kanalen i stedet for å kutte hjørner.
const CRUISE_WAYPOINTS_OUT = [
  [-123.112, 49.289],   // Vancouver (cr_emb)
  [-123.30, 49.36],     // ut av Burrard Inlet
  [-123.55, 49.50],     // English Bay
  [-123.80, 49.65],     // Strait of Georgia sør
  [-124.10, 49.80],     // Strait of Georgia midt-sør
  [-124.45, 49.95],     // Strait of Georgia midt
  [-124.85, 50.08],     // Strait of Georgia nord
  [-125.10, 50.20],     // Discovery Passage v/ Campbell River
  [-125.35, 50.32],     // Discovery Passage nord
  [-125.65, 50.45],     // mot Johnstone Strait
  [-126.00, 50.55],     // Johnstone Strait vest-inngang
  [-126.40, 50.60],     // Johnstone Strait midt-øst
  [-126.85, 50.65],     // Johnstone Strait midt-vest
  [-127.30, 50.75],     // Queen Charlotte Strait øst
  [-127.75, 50.85],     // Queen Charlotte Strait midt
  [-128.20, 51.00],     // Queen Charlotte Sound sør-øst
  [-128.70, 51.30],     // Queen Charlotte Sound midt
  [-129.20, 51.70],     // Queen Charlotte Sound nord
  [-129.70, 52.10],     // mot Hecate Strait sør
  [-130.15, 52.50],     // Hecate Strait sør (mellom mainland og Haida Gwaii)
  [-130.50, 52.90],     // Hecate Strait midt-sør
  [-130.75, 53.30],     // Hecate Strait midt
  [-130.85, 53.70],     // Hecate Strait nord-midt
  [-130.90, 54.10],     // Hecate Strait nord
  [-131.10, 54.40],     // mot Dixon Entrance
  [-131.50, 54.55],     // Dixon Entrance sør-øst
  [-132.10, 54.65],     // Dixon Entrance midt
  [-132.80, 54.80],     // Dixon Entrance vest
  [-133.40, 55.10],     // mot Sumner Strait sør
  [-133.80, 55.50],     // åpent vest for Prince of Wales
  [-134.20, 55.90],     // Pacific øst for Baranof
  [-134.60, 56.40],     // mot Chatham Strait sør
  [-134.85, 56.90],     // Chatham Strait sør
  [-135.00, 57.40],     // Chatham Strait midt-sør
  [-135.10, 57.80],     // Chatham Strait nord
  [-135.30, 58.05],     // mot Icy Strait
  [-135.50, 58.13],     // Icy Strait vest
  [-135.458, 58.129],   // Icy Strait Point
];

// Icy Strait → Skagway (via Lynn Canal nordover)
const CRUISE_TO_SKAGWAY = [
  [-135.458, 58.129],   // Icy Strait Point
  [-135.30, 58.25],     // Icy Strait, øst
  [-135.10, 58.40],     // mot Chatham Strait
  [-135.00, 58.55],     // inngang Lynn Canal sør
  [-135.05, 58.75],     // Lynn Canal, sør-midt
  [-135.15, 58.95],     // Lynn Canal, midt
  [-135.22, 59.15],     // Lynn Canal, nord-midt
  [-135.28, 59.30],     // Lynn Canal, nord
  [-135.314, 59.458],   // Skagway
];

// Skagway → Juneau (sør langs Lynn Canal + inn Gastineau Channel)
const CRUISE_TO_JUNEAU = [
  [-135.314, 59.458],   // Skagway
  [-135.25, 59.20],     // Lynn Canal nord
  [-135.15, 58.95],     // Lynn Canal midt
  [-135.05, 58.70],     // Lynn Canal sør
  [-134.95, 58.55],     // Stephens Passage nord-vest
  [-134.75, 58.45],     // mot Gastineau Channel
  [-134.55, 58.35],     // Gastineau Channel inngang
  [-134.419, 58.301],   // Juneau
];

// Juneau → Ketchikan — gjennom Chatham Strait (vest for Admiralty)
// så vi følger åpent farvann, deretter Clarence Strait åpent vest om
// Prince of Wales, og inn til Ketchikan fra sør.
const CRUISE_TO_KETCHIKAN = [
  [-134.419, 58.301],   // Juneau
  [-134.55, 58.15],     // ut Gastineau Channel sør
  [-134.80, 57.95],     // mot Chatham Strait
  [-134.95, 57.65],     // Chatham Strait nord
  [-135.00, 57.30],     // Chatham Strait midt
  [-134.95, 57.00],     // Chatham Strait sør
  [-134.80, 56.70],     // mot Sumner Strait sør
  [-134.50, 56.45],     // Sumner Strait vest
  [-134.20, 56.30],     // Sumner Strait midt-vest
  [-133.90, 56.20],     // Sumner Strait midt-øst
  [-133.50, 56.05],     // mot Clarence Strait
  [-133.10, 55.85],     // Clarence Strait nord-vest
  [-132.80, 55.60],     // Clarence Strait vest
  [-132.50, 55.30],     // Clarence Strait midt-vest
  [-132.20, 55.00],     // Clarence Strait sør-vest
  [-132.00, 54.85],     // Clarence Strait sør
  [-131.80, 54.95],     // mot Revillagigedo
  [-131.65, 55.10],     // sør Revillagigedo Channel
  [-131.55, 55.20],     // Revillagigedo Channel
  [-131.55, 55.28],     // Tongass Narrows sør
  [-131.60, 55.32],     // Tongass Narrows midt
  [-131.647, 55.341],   // Ketchikan
];

// Ketchikan → Vancouver — speilet av outbound, gjennom Inside Passage.
const CRUISE_RETURN = [
  [-131.647, 55.341],   // Ketchikan
  [-131.60, 55.32],     // Tongass Narrows ut
  [-131.55, 55.20],     // Revillagigedo Channel
  [-131.65, 55.05],     // Revillagigedo Channel sør
  [-131.85, 54.85],     // mot Dixon Entrance
  [-132.10, 54.65],     // Dixon Entrance midt
  [-131.80, 54.45],     // Dixon Entrance øst
  [-131.30, 54.30],     // mot Hecate Strait nord
  [-131.05, 54.05],     // Hecate Strait nord
  [-130.90, 53.70],     // Hecate Strait midt-nord
  [-130.80, 53.30],     // Hecate Strait midt
  [-130.55, 52.90],     // Hecate Strait midt-sør
  [-130.20, 52.50],     // Hecate Strait sør
  [-129.75, 52.10],     // mot Queen Charlotte Sound
  [-129.25, 51.70],     // Queen Charlotte Sound nord
  [-128.75, 51.30],     // Queen Charlotte Sound midt
  [-128.25, 51.00],     // Queen Charlotte Sound sør-øst
  [-127.80, 50.85],     // Queen Charlotte Strait midt
  [-127.35, 50.75],     // Queen Charlotte Strait øst
  [-126.90, 50.68],     // Johnstone Strait vest
  [-126.45, 50.62],     // Johnstone Strait midt-vest
  [-126.00, 50.55],     // Johnstone Strait midt
  [-125.60, 50.45],     // Johnstone Strait øst
  [-125.30, 50.32],     // Discovery Passage nord
  [-125.05, 50.20],     // Discovery Passage v/ Campbell River
  [-124.80, 50.05],     // Strait of Georgia nord
  [-124.40, 49.92],     // Strait of Georgia midt
  [-124.05, 49.78],     // Strait of Georgia midt-sør
  [-123.75, 49.62],     // Strait of Georgia sør
  [-123.50, 49.50],     // English Bay
  [-123.30, 49.36],     // mot Burrard Inlet
  [-123.112, 49.289],   // Vancouver (cr_deb)
];

// Hvor langt i hvert segment er reisen?
// Cruise: dag 3 (embark) til dag 10 (debark) = 7 dager
// Biltur: dag 10 (Kamloops) til dag 15 (Calgary) = 5 dager
function progressRatio(tripDay, startDay, endDay){
  if(tripDay < 0) return 0;
  if(tripDay >= endDay) return 1;
  if(tripDay <= startDay) return 0;
  return (tripDay - startDay) / (endDay - startDay);
}

function makeRoutes(stops, tripDay){
  const byId = Object.fromEntries(stops.map(s => [s.id, s]));
  const lineTo = (ids) => ids
    .map(id => byId[id])
    .filter(Boolean)
    .map(s => [s.lng, s.lat]);

  const cruiseFull = [
    ...CRUISE_WAYPOINTS_OUT,
    ...CRUISE_TO_SKAGWAY.slice(1),
    ...CRUISE_TO_JUNEAU.slice(1),
    ...CRUISE_TO_KETCHIKAN.slice(1),
    ...CRUISE_RETURN.slice(1),
  ];
  const carFull = lineTo(['yvr','kam','jas','ban','yyc']);

  const cruiseProg = buildProgressiveTrail(cruiseFull, progressRatio(tripDay, 2, 9));
  const carProg    = buildProgressiveTrail(carFull,    progressRatio(tripDay, 9, 14));

  // Fly har binær progresjon (enten reist eller ikke)
  const flewOut  = tripDay >= 0;
  const flewHome = tripDay >= 17;

  return [
    // Hjem→YVR fly
    {
      id: 'route-fly-out',
      coords: lineTo(['osl','yvr']),
      paint: {
        'line-color': COLORS.maple,
        'line-width': 2,
        'line-dasharray': flewOut ? [1] : [3, 2],
        'line-opacity': flewOut ? 0.7 : 0.35,
      },
    },
    // Cruise — fremtid (lys, stiplet)
    {
      id: 'route-cruise-left',
      coords: cruiseProg.left,
      paint: { 'line-color': COLORS.navy, 'line-width': 2, 'line-dasharray': [4, 3], 'line-opacity': 0.40 },
    },
    // Cruise — kjørt (solid navy)
    {
      id: 'route-cruise-done',
      coords: cruiseProg.done,
      paint: { 'line-color': COLORS.navy, 'line-width': 2.8, 'line-opacity': 0.95 },
    },
    // Biltur — fremtid
    {
      id: 'route-car-left',
      coords: carProg.left,
      paint: { 'line-color': COLORS.maple, 'line-width': 2, 'line-dasharray': [3, 2], 'line-opacity': 0.45 },
    },
    // Biltur — kjørt (solid maple)
    {
      id: 'route-car-done',
      coords: carProg.done,
      paint: { 'line-color': COLORS.maple, 'line-width': 3.2, 'line-opacity': 0.95 },
    },
    // YYC→hjem fly
    {
      id: 'route-fly-home',
      coords: lineTo(['yyc_dep','osl']),
      paint: {
        'line-color': COLORS.maple,
        'line-width': 2,
        'line-dasharray': flewHome ? [1] : [3, 2],
        'line-opacity': flewHome ? 0.7 : 0.35,
      },
    },
  ];
}

function addRouteLayers(map, routes){
  for(const r of routes){
    if(r.coords.length < 2) continue;
    if(map.getSource(r.id)) map.removeSource(r.id);
    map.addSource(r.id, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: r.coords },
      },
    });
    if(map.getLayer(r.id)) map.removeLayer(r.id);
    map.addLayer({
      id: r.id,
      type: 'line',
      source: r.id,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: r.paint,
    });
  }
}

// Forventet vær-glyf per stopp basert på klima/årstid (juli–august)
const STOP_WEATHER = {
  yvr:    { g: '☀', t: '18–24°C' },   // Vancouver sommer
  cr_emb: { g: '☀', t: '18°C' },
  icy:    { g: '☁', t: '12°C' },      // Alaska kyst kjølig
  skw:    { g: '☂', t: '12°C' },
  jun:    { g: '☂', t: '14°C' },
  ktn:    { g: '☂', t: '15°C' },
  cr_deb: { g: '☀', t: '20°C' },
  kam:    { g: '☼', t: '28°C' },      // Kamloops varmt
  jas:    { g: '◐', t: '15°C' },
  ban:    { g: '◐', t: '16°C' },
  yyc:    { g: '☀', t: '22°C' },
};

function buildMarker(stop, dayNum, isToday){
  const el = document.createElement('div');
  el.className = 'fj-map-pin' + (isToday ? ' fj-map-pin-today' : '');
  el.dataset.stopId = stop.id;
  const w = STOP_WEATHER[stop.id];
  const weatherChip = w ? `<span class="fj-map-pin-weather" aria-hidden="true">${w.g}</span>` : '';
  el.innerHTML = `<span class="fj-map-pin-num">${dayNum}</span>${weatherChip}`;
  el.title = `${stop.name} · ${stop.d}${w ? ' · ' + w.t : ''}`;
  return el;
}

// Eksplisitt mapping fra stop.id til dag-nummer (1-indexed) — vises på pinnen
const STOP_TO_DAY = {
  yvr:    1,   // Vancouver
  cr_emb: 3,   // Embarkering
  icy:    5,   // Icy Strait
  skw:    6,   // Skagway
  jun:    7,   // Juneau
  ktn:    8,   // Ketchikan
  cr_deb: 10,  // Debarkering
  kam:    10,  // Kamloops
  jas:    11,  // Jasper
  ban:    13,  // Banff
  yyc:    15,  // Calgary
};

function dayNumForStop(stop){
  return STOP_TO_DAY[stop.id] ?? null;
}

function isStopToday(stop, tripDay){
  if(tripDay < 0) return false;
  const num = STOP_TO_DAY[stop.id];
  return num != null && num === tripDay + 1;
}

// Kuraterte POI-er underveis. Hver POI har:
//   name, lat, lng, kind (park/city/glacier/mountain/lake/eat/view)
//   dayIdx — hvilken dag det hører til
//   desc — kort beskrivelse vist i info-panel ved klikk
//   wiki — Wikipedia-tittel for Commons-bilde-oppslag (valgfritt)
const POIS = [
  // Vancouver (dag 1–2)
  { name: 'Stanley Park',         lat: 49.302,  lng: -123.144, kind: 'park',     dayIdx: 1,  wiki: 'Stanley_Park',
    desc: '400 hektar bypark med Seawall (10 km kystpromenade), totempåler ved Brockton Point og Lions Gate Bridge-utsikt.' },
  { name: 'Granville Island',     lat: 49.272,  lng: -123.135, kind: 'city',     dayIdx: 1,  wiki: 'Granville_Island',
    desc: 'Markedsø under Granville Bridge — Public Market, kunsthåndverkere, mikrobryggerier. Best før kl 11.' },
  { name: 'Capilano Bridge',      lat: 49.343,  lng: -123.114, kind: 'park',     dayIdx: 1,  wiki: 'Capilano_Suspension_Bridge',
    desc: '137 m lang hengebro 70 m over Capilano River. Cliffwalk og Treetops Adventure inkl.' },
  { name: 'Gastown',              lat: 49.284,  lng: -123.108, kind: 'eat',      dayIdx: 1,  wiki: 'Gastown',
    desc: 'Historisk bydel med damp-klokke, brostein og restaurantgater. Anbefalt: L\'Abattoir, Phnom Penh, Meat & Bread.' },
  // Inside Passage / Alaska
  { name: 'Mendenhall Glacier',   lat: 58.435,  lng: -134.545, kind: 'glacier',  dayIdx: 6,  wiki: 'Mendenhall_Glacier',
    desc: 'Iskappe 21 km lang, terminerer i Mendenhall Lake. Visitor center + Nugget Falls Trail (3 km t/r) er gratis.' },
  { name: 'White Pass',           lat: 59.621,  lng: -135.143, kind: 'mountain', dayIdx: 5,  wiki: 'White_Pass',
    desc: 'Gullrush-passasje på 873 m. White Pass & Yukon Route-toget tar dere fra Skagway til toppen — bestilt for dag 5.' },
  { name: 'Endicott Arm',         lat: 57.738,  lng: -133.117, kind: 'glacier',  dayIdx: 6,
    desc: 'Fjord 50 km lang inn til Dawes Glacier. Cruiset seiler inn tidlig morgen — gå på dekk kl 05:30.' },
  { name: 'Misty Fjords',         lat: 55.617,  lng: -130.667, kind: 'park',     dayIdx: 7,  wiki: 'Misty_Fjords_National_Monument',
    desc: 'Tongass National Forest — 900 m vertikale klipper og fossefall. Best opplevd med sjøfly fra Ketchikan.' },
  { name: 'Creek Street',         lat: 55.341,  lng: -131.647, kind: 'eat',      dayIdx: 7,  wiki: 'Creek_Street',
    desc: 'Tidligere red-light-distrikt, nå butikker og restauranter på påler over Ketchikan Creek. Spis Annabelle\'s for laks.' },
  // Biltur Vancouver → Kamloops (dag 9, Hwy 5)
  { name: 'Hope',                 lat: 49.382,  lng: -121.441, kind: 'view',     dayIdx: 9,  wiki: 'Hope,_British_Columbia',
    desc: 'Lite tettsted ved Fraser River — siste storby før Coquihalla Highway. Pause for kaffe ved Blue Moose Coffee.' },
  { name: 'Othello Tunnels',      lat: 49.378,  lng: -121.379, kind: 'park',     dayIdx: 9,  wiki: 'Othello_Tunnels',
    desc: 'Fem jernbanetunneler gjennom Coquihalla Canyon. 3,5 km flat sti — best stopp på Hwy 5. 30 min, gratis.' },
  { name: 'Home Restaurant Hope', lat: 49.381,  lng: -121.435, kind: 'eat',      dayIdx: 9,
    desc: 'Diner-klassiker rett ved Hwy 5-avkjøringen i Hope. Frokost-burger og milkshakes — ferry-trip-vibe.' },
  { name: 'Coquihalla Pass',      lat: 49.594,  lng: -121.124, kind: 'mountain', dayIdx: 9,
    desc: '1244 m høyt fjellpass på Hwy 5. Snø året rundt på toppene — utsiktspunkt nord for tunnelene.' },
  { name: 'Britton Creek Rest',   lat: 49.756,  lng: -120.890, kind: 'view',     dayIdx: 9,
    desc: 'Rasteplass med toaletter og piknikbord. Eneste sivilisasjon mellom Hope og Merritt — bensin på Merritt etterpå.' },
  // Kamloops → Jasper (dag 10, Hwy 5)
  { name: 'Mount Robson',         lat: 53.107,  lng: -119.155, kind: 'mountain', dayIdx: 10, wiki: 'Mount_Robson',
    desc: 'Høyeste fjell i Canadian Rockies (3954 m). Visitor center med kafé og Berg Lake Trail — første km enkel familievandring.' },
  { name: 'Rearguard Falls',      lat: 53.025,  lng: -119.349, kind: 'park',     dayIdx: 10, wiki: 'Rearguard_Falls_Provincial_Park',
    desc: 'Siste foss laksene klatrer — chinooks hopper opp i august/september. Kort tur fra parkering, 15 min.' },
  { name: 'Valemount Lookout',    lat: 52.834,  lng: -119.273, kind: 'view',     dayIdx: 10,
    desc: 'Utsikt over Robson Valley fra Hwy 5. Stopp før Tete Jaune Cache-avkjørselen. Tabell med fjellnavn.' },
  // Jasper (dag 11)
  { name: 'Athabasca Falls',      lat: 52.665,  lng: -117.884, kind: 'park',     dayIdx: 11, wiki: 'Athabasca_Falls',
    desc: '23 m fossefall i et smalt juv. Sti-system rundt med flere viewpoints — 30 min besøk er nok.' },
  { name: 'Maligne Lake',         lat: 52.745,  lng: -117.642, kind: 'lake',     dayIdx: 11, wiki: 'Maligne_Lake',
    desc: '22 km langt fjellvann. Båttur til Spirit Island (1,5 t) er ikoniske Banff-postkortet. Bestill dagen før.' },
  { name: 'Jasper SkyTram',       lat: 52.832,  lng: -118.137, kind: 'mountain', dayIdx: 11, wiki: 'Jasper_SkyTram',
    desc: 'Gondol opp Whistlers Mountain (2277 m). 360°-utsikt + sti til toppen (1,4 km, bratt).' },
  // Icefields Parkway (dag 12, Jasper → Lake Louise)
  { name: 'Sunwapta Falls',       lat: 52.534,  lng: -117.643, kind: 'park',     dayIdx: 12, wiki: 'Sunwapta_Falls',
    desc: 'Tvedelt fossefall der Sunwapta River møter Athabasca. Bro over selve fallet — 10 min stopp.' },
  { name: 'Tangle Creek Falls',   lat: 52.288,  lng: -117.197, kind: 'park',     dayIdx: 12,
    desc: 'Foss rett ved Hwy 93 — ingen sti, bare gå ut av bilen. Lett å miste hvis du ikke følger med på km-skiltene.' },
  { name: 'Columbia Icefield',    lat: 52.219,  lng: -117.227, kind: 'glacier',  dayIdx: 12, wiki: 'Columbia_Icefield',
    desc: 'Største iskappe i Rockies. Ice Explorer-buss ut på Athabasca Glacier + Skywalk-platform. Bestill tidlig.' },
  { name: 'Wilcox Pass Trail',    lat: 52.205,  lng: -117.211, kind: 'mountain', dayIdx: 12,
    desc: 'Beste utsikt over Athabasca Glacier — fra ovenfra. 4 km t/r, 350 hm. Ofte bighorn sheep på stien.' },
  { name: 'Saskatchewan Crossing', lat: 51.972, lng: -116.732, kind: 'eat',      dayIdx: 12,
    desc: 'Halvveis-stopp mellom Jasper og Lake Louise. Eneste bensin + restaurant på 233 km. Fyll alltid her.' },
  { name: 'Peyto Lake',           lat: 51.726,  lng: -116.523, kind: 'lake',     dayIdx: 12, wiki: 'Peyto_Lake',
    desc: 'Turkis-blå glacierfed innsjø. 1,4 km tur fra Bow Summit-parkering. Best lys 10–13.' },
  { name: 'Bow Lake',             lat: 51.668,  lng: -116.475, kind: 'lake',     dayIdx: 12, wiki: 'Bow_Lake_(Alberta)',
    desc: 'Stor glacier-innsjø med Num-Ti-Jah Lodge. Lite folk på morgen og kveld — gå rundt nordsiden.' },
  { name: 'Crowfoot Glacier',     lat: 51.640,  lng: -116.455, kind: 'glacier',  dayIdx: 12, wiki: 'Crowfoot_Glacier',
    desc: 'Hengebre rett ved veien. Lett synlig fra utkikkspunkt på Hwy 93 — 5 min stopp.' },
  // Lake Louise / Moraine (dag 13)
  { name: 'Lake Louise',          lat: 51.426,  lng: -116.176, kind: 'lake',     dayIdx: 13, wiki: 'Lake_Louise',
    desc: 'Ikonisk turkis innsjø foran Victoria Glacier. Fairmont Chateau på land-siden. Parkering full innen 08:00 — shuttle anbefalt.' },
  { name: 'Moraine Lake',         lat: 51.327,  lng: -116.183, kind: 'lake',     dayIdx: 13, wiki: 'Moraine_Lake',
    desc: 'Sett på 20-dollarseddelen. Bare shuttle siden 2023 — bestilles på parkscanada.gc.ca dagen før. Solen treffer Ten Peaks ca 07:15.' },
  // Banff (dag 14)
  { name: 'Banff Gondola',        lat: 51.149,  lng: -115.572, kind: 'mountain', dayIdx: 14, wiki: 'Banff_Gondola',
    desc: 'Gondol opp Sulphur Mountain (2281 m). Boardwalk + restaurant på toppen. Booker opp før kl 11 — bestill online.' },
  { name: 'Banff Avenue',         lat: 51.178,  lng: -115.570, kind: 'eat',      dayIdx: 14, wiki: 'Banff,_Alberta',
    desc: 'Hovedgaten med restauranter og butikker. Anbefalt: Three Ravens (fine dining), Park Distillery (whiskey + steak).' },
  { name: 'Cave & Basin',         lat: 51.169,  lng: -115.587, kind: 'park',     dayIdx: 14, wiki: 'Cave_and_Basin_National_Historic_Site',
    desc: 'Kilden Canadas nasjonalparksystem startet med — varme svovelkilder oppdaget i 1883. 30 min besøk + Banff-historie.' },
  { name: 'Surprise Corner',      lat: 51.171,  lng: -115.554, kind: 'view',     dayIdx: 14,
    desc: 'Klassisk utsiktspunkt over Banff Springs Hotel og Bow Valley. 3 min fra Banff Avenue — best i morgenlys.' },
  { name: 'Vermilion Lakes',      lat: 51.180,  lng: -115.605, kind: 'lake',     dayIdx: 14, wiki: 'Vermilion_Lakes',
    desc: 'Tre stille innsjøer rett vest for Banff. Speilbilder av Mount Rundle ved soloppgang/-nedgang. Bilvei 4,3 km langs nordsiden.' },
  { name: 'Lake Minnewanka',      lat: 51.222,  lng: -115.469, kind: 'lake',     dayIdx: 14, wiki: 'Lake_Minnewanka',
    desc: 'Største innsjø i Banff (28 km). Båttur 1 t for $60/pers, eller bare kjør Minnewanka Loop — bighorn sheep ofte ved veien.' },
  { name: 'Park Distillery',      lat: 51.178,  lng: -115.572, kind: 'eat',      dayIdx: 14,
    desc: 'Distilleri + bistro på Banff Avenue. Vill kjøtt, rye whisky destillert på stedet, og overraskende rimelig for Banff. Bestill bord.' },
  // Calgary (dag 15)
  { name: 'Stephen Avenue',       lat: 51.046,  lng: -114.066, kind: 'eat',      dayIdx: 15, wiki: 'Stephen_Avenue',
    desc: 'Bilfri gågate i Calgary downtown. Anbefalt: Charcut (kjøtt), Anju (koreansk), Model Milk (fine dining).' },
  { name: 'Calgary Tower',        lat: 51.044,  lng: -114.062, kind: 'view',     dayIdx: 15, wiki: 'Calgary_Tower',
    desc: '191 m utsiktstårn med roterende restaurant og glass-gulv. Best ved solnedgang for prairie+Rockies-utsikt.' },
];

const POI_ICON = {
  park:     '▲',
  city:     '◆',
  glacier:  '◇',
  mountain: '△',
  lake:     '○',
  eat:      '☆',
  view:     '◉',
};

function addPoiMarkers(map){
  POIS.forEach(p => {
    const el = document.createElement('div');
    el.className = 'fj-map-poi';
    el.dataset.kind = p.kind;
    el.innerHTML = `<span class="fj-map-poi-glyph">${POI_ICON[p.kind] || '•'}</span><span class="fj-map-poi-lbl">${p.name}</span>`;
    el.title = `${p.name} — Dag ${p.dayIdx + 1}`;
    el.style.cursor = 'pointer';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      openPoiInfo(p);
    });
    new maplibregl.Marker({ element: el, anchor: 'left' })
      .setLngLat([p.lng, p.lat])
      .addTo(map);
    markerEls.push(el);
  });
}

// Wikimedia Commons API — henter første relevante bilde for en Wikipedia-side
async function fetchWikiImage(title){
  try {
    const url = `https://en.wikipedia.org/w/api.php?action=query&prop=pageimages&format=json&pithumbsize=600&titles=${encodeURIComponent(title)}&origin=*`;
    const r = await fetch(url);
    const j = await r.json();
    const pages = j.query?.pages;
    if(!pages) return null;
    const first = Object.values(pages)[0];
    return first?.thumbnail?.source || null;
  } catch { return null; }
}

let _poiInfoEl = null;
function ensurePoiPanel(){
  if(_poiInfoEl) return _poiInfoEl;
  _poiInfoEl = document.createElement('div');
  _poiInfoEl.className = 'fj-poi-info';
  _poiInfoEl.innerHTML = '<button class="fj-poi-info-close" aria-label="Lukk">×</button><div class="fj-poi-info-body"></div>';
  _poiInfoEl.querySelector('.fj-poi-info-close').addEventListener('click', () => {
    _poiInfoEl.classList.remove('open');
  });
  document.body.appendChild(_poiInfoEl);
  return _poiInfoEl;
}

async function openPoiInfo(p){
  const panel = ensurePoiPanel();
  const body = panel.querySelector('.fj-poi-info-body');
  const dayLabel = typeof p.dayIdx === 'number' ? `Dag ${p.dayIdx + 1}` : '';
  const localImg = poiLocalImg(p.name);
  // Lokalt bilde vises umiddelbart (offline-vennlig); ellers Wikipedia-skjelett.
  const imgBlock = localImg
    ? `<div class="fj-poi-info-img-wrap"><img src="${esc(localImg)}" alt="${esc(p.name)}" loading="lazy"></div>`
    : (p.wiki ? '<div class="fj-poi-info-img-wrap"><div class="fj-poi-info-img-skel">Henter bilde …</div></div>' : '');
  body.innerHTML = `
    <div class="fj-poi-info-eyebrow">${dayLabel}</div>
    <h3 class="fj-poi-info-title">${p.name}</h3>
    ${imgBlock}
    <p class="fj-poi-info-desc">${p.desc || ''}</p>
    ${typeof p.dayIdx === 'number' ? `<button class="fj-poi-info-action" data-day="${p.dayIdx}">Åpne i dagsplanen →</button>` : ''}
    ${p.wiki ? `<a class="fj-poi-info-wiki" href="https://en.wikipedia.org/wiki/${encodeURIComponent(p.wiki)}" target="_blank" rel="noopener">Mer på Wikipedia →</a>` : ''}
  `;
  panel.classList.add('open');

  // «Åpne i dagsplanen» — gammel oppførsel som sekundær handling
  body.querySelector('[data-day]')?.addEventListener('click', () => {
    const idx = Number(body.querySelector('[data-day]').dataset.day);
    panel.classList.remove('open');
    if(typeof window.setTab === 'function'){
      window.setTab('reisen', 'plan');
      if(typeof window.S !== 'undefined') window.S.openDay = idx;
      if(typeof window.renderPage === 'function') window.renderPage();
      setTimeout(() => {
        document.getElementById('day-' + idx)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  });

  // Hent Wikimedia-bilde asynkront (kun når vi ikke har et lokalt bilde)
  if(p.wiki && !localImg){
    const wrap = body.querySelector('.fj-poi-info-img-wrap');
    const src = await fetchWikiImage(p.wiki);
    if(src && wrap){
      wrap.innerHTML = `<img src="${esc(safeUrl(src))}" alt="${esc(p.name)}" loading="lazy">`;
    } else if(wrap){
      wrap.style.display = 'none';
    }
  }
}

// Progressivt spor: dele cruise og biltur i «kjørt» og «gjenstår»
// basert på tripDay. Solid maple/aurora for kjørt, lys stiplet for fremtid.
function buildProgressiveTrail(allCoords, ratio){
  if(ratio <= 0) return { done: [], left: allCoords };
  if(ratio >= 1) return { done: allCoords, left: [] };
  const splitIdx = Math.floor(allCoords.length * ratio);
  return {
    done: allCoords.slice(0, splitIdx + 1),
    left: allCoords.slice(splitIdx),
  };
}

let gpsMarker = null;
let gpsWatchId = null;

export function toggleGpsOnMap(){
  if(!mapInstance) return false;
  if(gpsWatchId != null){
    navigator.geolocation.clearWatch(gpsWatchId);
    gpsWatchId = null;
    if(gpsMarker){ gpsMarker.remove(); gpsMarker = null; }
    return false;
  }
  if(!navigator.geolocation) return false;
  gpsWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords;
      if(!gpsMarker){
        const el = document.createElement('div');
        el.className = 'fj-map-gps';
        el.innerHTML = '<span class="fj-map-gps-pulse"></span><span class="fj-map-gps-dot"></span>';
        gpsMarker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([longitude, latitude])
          .addTo(mapInstance);
        mapInstance.flyTo({ center: [longitude, latitude], zoom: 7, duration: 1200 });
      } else {
        gpsMarker.setLngLat([longitude, latitude]);
      }
    },
    (err) => console.warn('GPS error', err),
    { enableHighAccuracy: true, maximumAge: 30000, timeout: 12000 }
  );
  return true;
}

export function destroyFeltjournalMap(){
  if(mapInstance){
    try { mapInstance.remove(); } catch {}
    mapInstance = null;
  }
  markerEls.forEach(m => { try { m.remove(); } catch {} });
  markerEls = [];
}

export function renderFeltjournalMap(containerId, stops, tripDay){
  if(typeof maplibregl === 'undefined'){
    console.warn('MapLibre ikke lastet');
    return null;
  }
  const container = document.getElementById(containerId);
  if(!container) return null;

  destroyFeltjournalMap();

  const map = new maplibregl.Map({
    container: containerId,
    style: buildStyle(),
    center: [-115, 54],
    zoom: 3.6,
    attributionControl: false,
  });
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

  map.on('load', () => {
    addRouteLayers(map, makeRoutes(stops, tripDay));

    // Markører
    stops.forEach(s => {
      if(s.id === 'osl') return;             // hjemmebane-flyplass — skjul
      const dayNum = dayNumForStop(s);
      if(dayNum == null) return;
      const isToday = isStopToday(s, tripDay);
      const el = buildMarker(s, dayNum, isToday);
      const m = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([s.lng, s.lat])
        .setPopup(new maplibregl.Popup({ offset: 18, closeButton: false }).setHTML(
          `<div class="fj-map-popup"><strong>${s.name}</strong><br><span>${s.d}</span></div>`
        ))
        .addTo(map);
      markerEls.push(el);
    });

    // Kuraterte POI-er
    addPoiMarkers(map);

    // Fit bounds rundt alle stopp utenom Oslo — vent på at containeren er målt
    const bounds = new maplibregl.LngLatBounds();
    stops.filter(s => s.id !== 'osl' && STOP_TO_DAY[s.id] != null)
      .forEach(s => bounds.extend([s.lng, s.lat]));
    requestAnimationFrame(() => {
      try { map.resize(); } catch {}
      map.fitBounds(bounds, { padding: 50, duration: 0, maxZoom: 5 });
    });
  });

  // Eksponer for ekstern resize ved fullskjerm-toggle
  container.parentElement && (container._maplibre = map);
  container._maplibre = map;

  mapInstance = map;
  return map;
}

// Eksponer en resize-helper for fullscreen-toggle
export function resizeMap(){
  if(mapInstance){ try { mapInstance.resize(); } catch {} }
}

export function getFeltjournalMap(){ return mapInstance; }

// Filter: 'all' | 'fly' | 'cruise' | 'car'
// Skjuler eller viser MapLibre-lag + tilhørende pinner
const FILTER_LAYERS = {
  all:    ['route-fly-out','route-cruise-left','route-cruise-done','route-car-left','route-car-done','route-fly-home'],
  fly:    ['route-fly-out','route-fly-home'],
  cruise: ['route-cruise-left','route-cruise-done'],
  car:    ['route-car-left','route-car-done'],
};

const FILTER_STOPS = {
  all:    null,                                         // alle synlige
  fly:    new Set(['yvr','yyc_dep']),
  cruise: new Set(['cr_emb','icy','skw','jun','ktn','cr_deb']),
  car:    new Set(['yvr','kam','jas','ban','yyc']),
};

const FILTER_POIS = {
  all:    null,
  fly:    new Set([]),
  cruise: new Set(['Mendenhall Glacier','White Pass','Endicott Arm','Misty Fjords']),
  car:    new Set([
    'Stanley Park','Granville Island','Capilano Bridge',
    'Athabasca Falls','Columbia Icefield','Peyto Lake','Bow Lake',
    'Lake Louise','Moraine Lake','Banff Gondola','Maligne Lake',
  ]),
};

let currentFilter = 'all';

// Bounding boxes for hver filter-modus — definerer hvilket utsnitt vi
// zoomer til når brukeren trykker filter-knappen.
const FILTER_BOUNDS = {
  // Alle: omtrent hele reiseområdet i Nord-Amerika-vest
  all:    { sw: [-138, 49], ne: [-112, 60] },
  // Fly: hele kloden mellom OSL og YVR/YYC — vis Atlanterhavet
  fly:    { sw: [-130, 48], ne: [15, 65] },
  // Cruise: BC kyst + Alaska panhandle
  cruise: { sw: [-138, 49], ne: [-128, 60] },
  // Biltur: Vancouver til Calgary med litt margin
  car:    { sw: [-124, 49], ne: [-113, 53.5] },
};

export function setMapFilterFj(filter){
  if(!mapInstance) return;
  currentFilter = filter;
  const allLayers = FILTER_LAYERS.all;
  const showLayers = new Set(FILTER_LAYERS[filter] || allLayers);
  allLayers.forEach(id => {
    if(mapInstance.getLayer(id)){
      mapInstance.setLayoutProperty(id, 'visibility', showLayers.has(id) ? 'visible' : 'none');
    }
  });
  // Pin- og POI-visibilitet via DOM
  const stopSet = FILTER_STOPS[filter];
  const poiSet = FILTER_POIS[filter];
  markerEls.forEach(el => {
    if(el.classList.contains('fj-map-pin')){
      const stopId = el.dataset.stopId;
      el.style.display = (!stopSet || stopSet.has(stopId)) ? '' : 'none';
    } else if(el.classList.contains('fj-map-poi')){
      const lbl = el.querySelector('.fj-map-poi-lbl')?.textContent;
      el.style.display = (!poiSet || poiSet.has(lbl)) ? '' : 'none';
    }
  });

  // Zoom til relevante stopp
  const b = FILTER_BOUNDS[filter];
  if(b){
    mapInstance.fitBounds(
      [b.sw, b.ne],
      { padding: 60, duration: 700, maxZoom: 7 }
    );
  }
}

export function getCurrentFilter(){ return currentFilter; }
